import type { ImageFile } from '~/features/shared/components/images-section'
import { draftStore } from '~/shared/lib/draft-store'
import { storageManager } from '~/shared/lib/storage-manager'

const DRAFT_VERSION = 1
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

type EventDraft<V> = {
  version: typeof DRAFT_VERSION
  savedAt: number
  values: V
  listingId: string | null | undefined
}

type StoredImage = { id: string; file: File }

const valuesKey = (businessId: string) => `pulsify-event-draft:${businessId}`
const imagesKey = (businessId: string) => `pulsify-event-draft-images:${businessId}`

export function readEventDraft<V>(businessId: string, now = Date.now()): EventDraft<V> | null {
  if (!businessId) return null
  const draft = storageManager.get<EventDraft<V>>(valuesKey(businessId))
  if (!draft || typeof draft !== 'object' || draft.version !== DRAFT_VERSION || !draft.values) {
    return null
  }
  if (typeof draft.savedAt !== 'number' || now - draft.savedAt > MAX_AGE_MS) {
    clearEventDraft(businessId)
    return null
  }
  return draft
}

export function hasEventDraft(businessId: string): boolean {
  return readEventDraft(businessId) !== null
}

export function writeEventDraft<V>(
  businessId: string,
  draft: { values: V; listingId: string | null | undefined },
  now = Date.now(),
): void {
  if (!businessId) return
  const stored: EventDraft<V> = { version: DRAFT_VERSION, savedAt: now, ...draft }
  storageManager.set(valuesKey(businessId), stored)
}

export function clearEventDraft(businessId: string): void {
  if (!businessId) return
  storageManager.remove(valuesKey(businessId))
  draftStore.remove(imagesKey(businessId))
}

export function coverFirst<T>(images: T[], coverIndex: number): T[] {
  const cover = images[coverIndex]
  if (!cover || coverIndex === 0) return images
  return [cover, ...images.filter((_, index) => index !== coverIndex)]
}

export async function saveDraftImages(businessId: string, images: ImageFile[]): Promise<void> {
  if (!businessId) return
  if (images.length === 0) {
    await draftStore.remove(imagesKey(businessId))
    return
  }
  const stored: StoredImage[] = images.map(({ id, file }) => ({ id, file }))
  await draftStore.write(imagesKey(businessId), stored)
}

export async function loadDraftImages(businessId: string): Promise<ImageFile[]> {
  if (!businessId) return []
  const stored = await draftStore.read<StoredImage[]>(imagesKey(businessId))
  if (!Array.isArray(stored)) return []
  return stored
    .filter((image) => image?.file instanceof Blob && typeof image.id === 'string')
    .map(({ id, file }) => ({ id, file, preview: URL.createObjectURL(file) }))
}
